import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Checkbox,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  Add,
  Refresh,
  MoreVert,
  Edit,
  Delete,
  FilterList,
  AssignmentOutlined,
  WatchLater,
  CheckCircleOutline,
} from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TypePill,
  ErrorState,
  TableEmptyState,
} from '../../components/common/pageKit';
import { useAuth } from '../../context/AuthContext';
import crmPlusService, {
  CrmTask,
  CreateTaskInput,
  TaskFilters,
  TaskPriority,
  TaskStatus,
  TaskEntityType,
  TASK_PRIORITIES,
  TASK_STATUSES,
  TASK_ENTITY_TYPES,
} from '../../services/crmPlusService';
import { EntityPicker } from '../../components/common/EntityPicker';

/** Identity colour per related-entity type — separate from status colour. */
const ENTITY_TINT: Record<TaskEntityType, string> = {
  lead: '#0284c7',
  opportunity: '#7c3aed',
  customer: '#059669',
  none: '#64748b',
};

/** Identity colour per priority — HIGH/URGENT read as hot (orange/red). */
const PRIORITY_TINT: Record<TaskPriority, string> = {
  LOW: '#64748b',
  MEDIUM: '#0284c7',
  HIGH: '#ea580c',
  URGENT: '#d03b3b',
};

const STATUS_TONE: Record<TaskStatus, StatusTone> = {
  OPEN: 'info',
  IN_PROGRESS: 'warning',
  DONE: 'success',
  CANCELLED: 'neutral',
};

const CHANGEABLE_STATUSES: TaskStatus[] = [...TASK_STATUSES];

const fmtDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('en-IN')} ${d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`;
};

const isOverdue = (task: CrmTask): boolean =>
  !!task.due_at &&
  task.status !== 'DONE' &&
  task.status !== 'CANCELLED' &&
  new Date(task.due_at).getTime() < Date.now();

/** Datetime-local input <-> ISO string helpers. */
const toDatetimeLocal = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

interface TaskFormData {
  title: string;
  description: string;
  relatedEntityType: TaskEntityType;
  relatedEntityId: string;
  priority: TaskPriority;
  dueAt: string;
  reminderAt: string;
}

const emptyForm: TaskFormData = {
  title: '',
  description: '',
  relatedEntityType: 'none',
  relatedEntityId: '',
  priority: 'MEDIUM',
  dueAt: '',
  reminderAt: '',
};

const RelatedEntityCell: React.FC<{ task: CrmTask; onOpen: (link: string) => void }> = ({ task, onOpen }) => {
  if (!task.related_entity_type || task.related_entity_type === 'none') {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  const tint = ENTITY_TINT[task.related_entity_type] || '#64748b';
  const label = task.related_entity_label || (task.related_entity_id ? `#${task.related_entity_id}` : '—');
  const clickable = !!task.link;
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: clickable ? 'pointer' : 'default' }}
      onClick={() => clickable && onOpen(task.link as string)}
    >
      <TypePill label={task.related_entity_type.toUpperCase()} tint={tint} />
      <Typography
        variant="body2"
        noWrap
        sx={{ maxWidth: 160, textDecoration: clickable ? 'underline' : 'none', textDecorationColor: 'divider' }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const TasksPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentUserId = user?.id ? Number(user.id) : NaN;

  const [tasks, setTasks] = useState<CrmTask[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [stats, setStats] = useState<{ open: number; overdue: number; doneThisWeek: number } | null>(null);

  const [statusFilter, setStatusFilter] = useState<TaskStatus | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority | ''>('');
  const [mineOnly, setMineOnly] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const [rowMenu, setRowMenu] = useState<{ anchor: HTMLElement; task: CrmTask } | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<CrmTask | null>(null);
  const [form, setForm] = useState<TaskFormData>(emptyForm);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<CrmTask | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: TaskFilters = {
        status: statusFilter || undefined,
        priority: priorityFilter || undefined,
        owner: mineOnly && !isNaN(currentUserId) ? currentUserId : undefined,
        overdue: overdueOnly || undefined,
        page: page + 1,
        limit: rowsPerPage,
      };
      const result = await crmPlusService.getTasks(filters);
      setTasks(result.tasks);
      setTotal(result.pagination?.total ?? result.tasks.length);
    } catch (err: any) {
      setError(err.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, mineOnly, overdueOnly, currentUserId, page, rowsPerPage]);

  const fetchStats = useCallback(async () => {
    try {
      setStats(await crmPlusService.getTaskStats());
    } catch {
      // Stat row simply stays hidden — never fake these numbers.
    }
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, priorityFilter, mineOnly, overdueOnly]);

  const refreshAll = () => {
    fetchTasks();
    fetchStats();
  };

  const openCreateDialog = () => {
    setEditingTask(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (task: CrmTask) => {
    setEditingTask(task);
    setForm({
      title: task.title,
      description: task.description || '',
      relatedEntityType: task.related_entity_type || 'none',
      relatedEntityId: task.related_entity_id ? String(task.related_entity_id) : '',
      priority: task.priority,
      dueAt: toDatetimeLocal(task.due_at),
      reminderAt: toDatetimeLocal(task.reminder_at),
    });
    setDialogOpen(true);
    setRowMenu(null);
  };

  const handleSaveTask = async () => {
    setSaving(true);
    try {
      const payload: CreateTaskInput = {
        title: form.title.trim(),
        description: form.description || undefined,
        relatedEntityType: form.relatedEntityType,
        relatedEntityId:
          form.relatedEntityType !== 'none' && form.relatedEntityId
            ? parseInt(form.relatedEntityId, 10)
            : null,
        priority: form.priority,
        dueAt: form.dueAt || null,
        reminderAt: form.reminderAt || null,
      };
      if (editingTask) {
        await crmPlusService.updateTask(editingTask.id, payload);
        setToast({ message: 'Task updated', severity: 'success' });
      } else {
        await crmPlusService.createTask(payload);
        setToast({ message: 'Task created', severity: 'success' });
      }
      setDialogOpen(false);
      refreshAll();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to save task', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleQuickToggleDone = async (task: CrmTask, checked: boolean) => {
    try {
      await crmPlusService.setTaskStatus(task.id, checked ? 'DONE' : 'OPEN');
      setToast({ message: checked ? 'Task marked done' : 'Task reopened', severity: 'success' });
      refreshAll();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to update status', severity: 'error' });
    }
  };

  const handleChangeStatus = async (task: CrmTask, status: TaskStatus) => {
    setRowMenu(null);
    try {
      await crmPlusService.setTaskStatus(task.id, status);
      setToast({ message: `Status changed to ${status}`, severity: 'success' });
      refreshAll();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to update status', severity: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await crmPlusService.deleteTask(deleteTarget.id);
      setToast({ message: 'Task deleted', severity: 'success' });
      setDeleteTarget(null);
      refreshAll();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to delete task', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const clearFilters = () => {
    setStatusFilter('');
    setPriorityFilter('');
    setMineOnly(false);
    setOverdueOnly(false);
  };

  const hasActiveFilters = useMemo(
    () => !!(statusFilter || priorityFilter || mineOnly || overdueOnly),
    [statusFilter, priorityFilter, mineOnly, overdueOnly]
  );

  return (
    <Box>
      <PageHeader
        title="Tasks"
        subtitle="Follow-ups and to-dos across leads, opportunities and customers"
        actions={
          <>
            <IconButton onClick={refreshAll} disabled={loading}>
              <Refresh />
            </IconButton>
            <Button variant="contained" startIcon={<Add />} size="small" onClick={openCreateDialog}>
              New Task
            </Button>
          </>
        }
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 2,
          mb: 2.5,
        }}
      >
        <StatCard title="Open" value={stats?.open ?? null} icon={<AssignmentOutlined />} color="#0284c7" />
        <StatCard title="Overdue" value={stats?.overdue ?? null} icon={<WatchLater />} color="#d03b3b" />
        <StatCard title="Done This Week" value={stats?.doneThisWeek ?? null} icon={<CheckCircleOutline />} color="#059669" />
      </Box>

      <FilterBar>
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as TaskStatus | '')}
          sx={{ minWidth: 160 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterList sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          {TASK_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Priority"
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value as TaskPriority | '')}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All Priorities</MenuItem>
          {TASK_PRIORITIES.map((p) => (
            <MenuItem key={p} value={p}>
              {p}
            </MenuItem>
          ))}
        </TextField>

        <FormControlLabel
          control={<Switch checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} size="small" />}
          label="Mine only"
        />
        <FormControlLabel
          control={<Switch checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} size="small" />}
          label="Overdue only"
        />

        <FilterSpacer />

        {hasActiveFilters && (
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </FilterBar>

      {error ? (
        <ErrorState message={error} onRetry={fetchTasks} />
      ) : (
        <TableSurface>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx} padding="checkbox" />
                  <TableCell sx={headerCellSx}>Title</TableCell>
                  <TableCell sx={headerCellSx}>Related Entity</TableCell>
                  <TableCell sx={headerCellSx}>Owner</TableCell>
                  <TableCell sx={headerCellSx}>Priority</TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                  <TableCell sx={headerCellSx}>Due</TableCell>
                  <TableCell sx={headerCellSx} align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, border: 0 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : tasks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ border: 0 }}>
                      <TableEmptyState title="No tasks — you're all clear" hint="Clear the filters, or create a new task." />
                    </TableCell>
                  </TableRow>
                ) : (
                  tasks.map((task) => {
                    const overdue = isOverdue(task);
                    return (
                      <TableRow key={task.id} sx={bodyRowSx}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={task.status === 'DONE'}
                            disabled={task.status === 'CANCELLED'}
                            onChange={(e) => handleQuickToggleDone(task, e.target.checked)}
                            inputProps={{ 'aria-label': `Mark "${task.title}" done` }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              textDecoration: task.status === 'DONE' ? 'line-through' : 'none',
                              color: task.status === 'DONE' ? 'text.disabled' : 'text.primary',
                            }}
                          >
                            {task.title}
                          </Typography>
                          {task.description && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 260 }}>
                              {task.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <RelatedEntityCell task={task} onOpen={(link) => navigate(link)} />
                        </TableCell>
                        <TableCell>{task.owner_name || '—'}</TableCell>
                        <TableCell>
                          <TypePill label={task.priority} tint={PRIORITY_TINT[task.priority]} />
                        </TableCell>
                        <TableCell>
                          <StatusDot label={task.status} tone={STATUS_TONE[task.status]} />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: overdue ? 700 : 400, color: overdue ? 'error.main' : 'text.primary' }}
                          >
                            {fmtDateTime(task.due_at)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            aria-label={`Actions for ${task.title}`}
                            onClick={(e) => setRowMenu({ anchor: e.currentTarget, task })}
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </TableSurface>
      )}

      {/* Row action menu */}
      <Menu anchorEl={rowMenu?.anchor || null} open={!!rowMenu} onClose={() => setRowMenu(null)}>
        <MenuItem onClick={() => rowMenu && openEditDialog(rowMenu.task)}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>

        <Divider />
        <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary' }}>
          Change status
        </Typography>
        {CHANGEABLE_STATUSES.map((s) => (
          <MenuItem
            key={s}
            onClick={() => rowMenu && handleChangeStatus(rowMenu.task, s)}
            disabled={rowMenu?.task.status === s}
          >
            <ListItemIcon>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: `${STATUS_TONE[s]}.main` }} />
            </ListItemIcon>
            <ListItemText>{s}</ListItemText>
          </MenuItem>
        ))}

        <Divider />
        <MenuItem
          onClick={() => {
            if (rowMenu) setDeleteTarget(rowMenu.task);
            setRowMenu(null);
          }}
        >
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTask ? 'Edit Task' : 'New Task'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Title"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              required
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                select
                fullWidth
                label="Related Entity Type"
                value={form.relatedEntityType}
                onChange={(e) =>
                  setForm((f) => ({ ...f, relatedEntityType: e.target.value as TaskEntityType, relatedEntityId: '' }))
                }
              >
                {TASK_ENTITY_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t === 'none' ? 'None' : t.charAt(0).toUpperCase() + t.slice(1)}
                  </MenuItem>
                ))}
              </TextField>
              {/* Was a free-text "Related Entity ID" number box. Now a
                  searchable lookup filtered to the chosen entity type. */}
              <EntityPicker
                entityType={form.relatedEntityType as any}
                value={form.relatedEntityId}
                onChange={(id) => setForm((f) => ({ ...f, relatedEntityId: id ? String(id) : '' }))}
                disabled={form.relatedEntityType === 'none'}
              />
            </Box>
            <TextField
              select
              fullWidth
              label="Priority"
              value={form.priority}
              onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value as TaskPriority }))}
            >
              {TASK_PRIORITIES.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Due At"
                type="datetime-local"
                value={form.dueAt}
                onChange={(e) => setForm((f) => ({ ...f, dueAt: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Reminder At"
                type="datetime-local"
                value={form.reminderAt}
                onChange={(e) => setForm((f) => ({ ...f, reminderAt: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveTask} disabled={saving || !form.title.trim()}>
            {saving ? 'Saving…' : editingTask ? 'Save Changes' : 'Create Task'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Task</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{deleteTarget?.title}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default TasksPage;
